import { motion } from "framer-motion";
import { Reveal, SectionTag, ArrowLink, MaskedLines, EASE } from "@/components/Reveal";

const VALUES = [
  { num: "01", name: "Ownership", desc: "Take responsibility for the work, the outcome and the details in between.", pos: "left-0 top-0", anchor: [24, 22] },
  { num: "02", name: "Execution", desc: "Turn clear thinking into disciplined, dependable action.", pos: "right-0 top-0", anchor: [76, 22] },
  { num: "03", name: "Curiosity", desc: "Ask better questions, understand the problem and keep learning.", pos: "left-0 bottom-0", anchor: [24, 78] },
  { num: "04", name: "Improvement", desc: "Look for practical ways to make systems, processes and experiences better.", pos: "right-0 bottom-0", anchor: [76, 78] },
];

/* desktop: four value nodes around a central concept, connections draw on scroll */
const ValuesHub = () => (
  <div
    data-testid="careers-visual-panel"
    className="relative hidden aspect-[10/8] w-full lg:block"
  >
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      {VALUES.map((v, i) => (
        <motion.line
          key={v.num}
          x1="50" y1="50" x2={v.anchor[0]} y2={v.anchor[1]}
          stroke="rgba(212,175,55,0.4)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.4 + i * 0.25 }}
        />
      ))}
      <motion.circle
        cx="50" cy="50" r="26" fill="none"
        stroke="rgba(212,175,55,0.16)" strokeWidth="1"
        strokeDasharray="2 4"
        vectorEffect="non-scaling-stroke"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.6, ease: EASE, delay: 1.4 }}
      />
    </svg>

    {/* centre concept */}
    <motion.div
      className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-center"
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
    >
      <motion.span
        className="mx-auto block h-[10px] w-[10px] rotate-45 bg-[#D4AF37]"
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.6 }}
        aria-hidden="true"
      />
      <p className="mt-4 text-[10px] font-medium uppercase tracking-[0.3em] text-[#E7CE8B]">
        Logibyte Way
        <br />
        of Working
      </p>
    </motion.div>

    {/* value nodes */}
    {VALUES.map((v, i) => (
      <motion.div
        key={v.num}
        data-testid={`careers-value-${v.name.toLowerCase()}`}
        className={`group absolute ${v.pos} w-[46%] border border-white/10 bg-[#12100A]/80 p-6 backdrop-blur-sm transition-colors duration-700 hover:border-[#D4AF37]/45 hover:bg-[#16130B]`}
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.5 + i * 0.22 }}
      >
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[11px] font-medium tracking-[0.25em] text-[#D4AF37]/80">{v.num}</span>
          <span
            className="h-2 w-2 rotate-45 bg-[#D4AF37]/40 transition-all duration-700 group-hover:bg-[#E7CE8B] group-hover:shadow-[0_0_10px_rgba(231,206,139,0.7)]"
            aria-hidden="true"
          />
        </div>
        <p className="mt-4 font-serif text-xl font-normal uppercase tracking-tight text-slate-200 transition-colors duration-700 group-hover:text-white">
          {v.name}
        </p>
        <p className="mt-2.5 text-[13px] font-normal leading-relaxed text-stone-400 transition-colors duration-700 group-hover:text-stone-300">
          {v.desc}
        </p>
      </motion.div>
    ))}
  </div>
);

/* mobile: vertical connected sequence */
const ValuesStack = () => (
  <div data-testid="careers-values-stack" className="relative mt-2 pl-10 lg:hidden">
    <motion.span
      className="absolute bottom-2 left-[5px] top-2 w-px origin-top bg-gradient-to-b from-[#E7CE8B] via-[#D4AF37]/40 to-white/10"
      initial={{ scaleY: 0 }}
      whileInView={{ scaleY: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.8, ease: EASE }}
      aria-hidden="true"
    />
    <ol className="space-y-8">
      {VALUES.map((v, i) => (
        <Reveal key={v.num} delay={0.1 + i * 0.1}>
          <li className="relative" data-testid={`careers-value-m-${v.name.toLowerCase()}`}>
            <span
              className="absolute -left-10 top-1.5 h-[10px] w-[10px] rotate-45 border border-[#D4AF37]/50 bg-[#0A0906]"
              aria-hidden="true"
            />
            <p className="text-[11px] font-medium tracking-[0.25em] text-[#D4AF37]/80">{v.num}</p>
            <p className="mt-1.5 font-serif text-xl font-normal uppercase tracking-tight text-slate-100">
              {v.name}
            </p>
            <p className="mt-2 max-w-sm text-sm font-normal leading-relaxed text-stone-400">
              {v.desc}
            </p>
          </li>
        </Reveal>
      ))}
    </ol>
  </div>
);

export default function Careers() {
  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="relative scroll-mt-20 overflow-hidden border-t border-white/5 bg-[#0A0906] py-20 sm:py-24"
    >
      {/* warm charcoal gradient + restrained architectural texture */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_90%_at_80%_10%,rgba(212,175,55,0.05),transparent_65%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)",
          backgroundSize: "88px 88px",
        }}
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-[0.45fr_0.55fr] lg:gap-16 lg:px-10">
        <div>
          <Reveal>
            <SectionTag index="14">Careers</SectionTag>
          </Reveal>
          <h2
            data-testid="careers-headline"
            className="mt-6 font-serif text-3xl font-normal tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
          >
            <MaskedLines delay={0.1}>Build What Comes Next.</MaskedLines>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-[17px] font-normal leading-[1.75] text-stone-300">
              We&apos;re interested in people who take ownership, execute with
              discipline, stay curious and continuously look for better ways to
              work.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-md text-[15px] font-normal leading-[1.75] text-stone-400">
              Logibyte Technologies brings together technology, operations and
              customer-focused execution. We value people who can think
              independently, work responsibly and contribute to building better
              systems and businesses.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <ArrowLink to="/careers" testid="careers-explore-link">
              Explore Careers
            </ArrowLink>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <ValuesHub />
          <ValuesStack />
        </Reveal>
      </div>

      {/* transition into Section 15: the value system collapses into one node */}
      <div className="relative mx-auto mt-16 flex max-w-7xl flex-col items-center px-6" aria-hidden="true">
        <motion.span
          className="h-14 w-px origin-top bg-gradient-to-b from-[#D4AF37]/50 to-[#D4AF37]/10"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.4, ease: EASE }}
        />
        <motion.span
          className="mt-[-1px] h-[9px] w-[9px] rotate-45 border border-[#D4AF37]/60 bg-[#0A0906]"
          initial={{ opacity: 0, scale: 0.3 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
        />
      </div>
    </section>
  );
}
